import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";
import { Field, Modal, QtyInput, StatusBadge, useToast } from "@cx/ui";
import type { FiscalDocumentView, FiscalEventView, FiscalFileLink } from "@cx/contracts";
import { CCE_CONDITIONS, eventTextProblem, formatQty, voidRangeProblem } from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import {
  EVENT_STATUS_LABEL,
  EVENT_STATUS_TONE,
  INVOICE_STATUS_LABEL,
  INVOICE_STATUS_TONE,
  eventInFlight,
  eventTitle,
  sefazMessage,
} from "./labels";

/**
 * Eventos da NF-e (T2.4): diálogos de cancelamento, carta de correção, devolução e inutilização, e a linha do tempo
 * dos eventos com XML/PDF. Toda escrita com uma Idempotency-Key por abertura do diálogo (duplo clique = um pedido).
 */

export const dateTimeBR = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });

/** Chave nova a cada abertura; recusa de validação (não 409) também troca (nada foi gravado). */
export function useOpenKey(open: boolean) {
  const [key, setKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (open) setKey(crypto.randomUUID());
  }, [open]);
  const renew = (e: Error) => {
    if (e instanceof ApiFailure && e.status !== 409) setKey(crypto.randomUUID());
  };
  return { key, renew };
}

type TextDialogProps = {
  open: boolean;
  doc: FiscalDocumentView;
  onClose(): void;
  onDone(d: FiscalDocumentView): void;
};

/** Justificativa do cancelamento (15 a 255 caracteres), com o prazo. */
export function CancelInvoiceDialog({ open, doc, onClose, onDone }: TextDialogProps): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const { key, renew } = useOpenKey(open);
  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);
  const problem = eventTextProblem(reason, "reason");
  const send = useMutation({
    mutationFn: () =>
      api<FiscalDocumentView>(`/fiscal-documents/${doc.id}/cancel`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (d) => {
      toast("info", "Cancelamento enviado à SEFAZ");
      onDone(d);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title="Cancelar NF-e"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={send.isPending}
            onClick={() => (problem ? setTouched(true) : send.mutate())}
          >
            {send.isPending ? "Enviando…" : "Cancelar NF-e"}
          </button>
        </>
      }
    >
      <p className="small">
        O cancelamento é um evento registrado na SEFAZ: a nota original não muda e passa a constar como
        cancelada. O estoque e as contas lançados por ela são estornados.
        {doc.cancelUntil && <> Prazo para cancelar: até {dateTimeBR(doc.cancelUntil)}.</>}
      </p>
      <div className="row">
        <Field
          label="Justificativa"
          required
          className="col-12"
          error={touched ? (problem ?? undefined) : undefined}
          hint={`${reason.trim().length}/255 caracteres (mínimo 15)`}
        >
          <textarea
            className="form-control"
            rows={3}
            maxLength={255}
            value={reason}
            data-autofocus
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Carta de correção (15 a 1000 caracteres) com as condições de uso (xCondUso) que vão no evento. */
export function CorrectionDialog({ open, doc, onClose, onDone }: TextDialogProps): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [text, setText] = useState("");
  const [touched, setTouched] = useState(false);
  const { key, renew } = useOpenKey(open);
  useEffect(() => {
    if (open) {
      setText("");
      setTouched(false);
    }
  }, [open]);
  const problem = eventTextProblem(text, "correction");
  const send = useMutation({
    mutationFn: () =>
      api<FiscalDocumentView>(`/fiscal-documents/${doc.id}/correction`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ text }),
      }),
    onSuccess: (d) => {
      toast("info", "Carta de correção enviada à SEFAZ");
      onDone(d);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title="Carta de correção"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending}
            onClick={() => (problem ? setTouched(true) : send.mutate())}
          >
            {send.isPending ? "Enviando…" : "Enviar carta de correção"}
          </button>
        </>
      }
    >
      <div className="alert alert-warning small" role="note" data-testid="cce-conditions">
        <strong>Condições de uso.</strong> {CCE_CONDITIONS}
      </div>
      <p className="small text-muted">
        Cada carta substitui a anterior: escreva a correção completa (todas as correções valem juntas).
        {doc.currentCorrection && <> Correção vigente: “{doc.currentCorrection}”.</>}
      </p>
      <div className="row">
        <Field
          label="Correção"
          required
          className="col-12"
          error={touched ? (problem ?? undefined) : undefined}
          hint={`${text.trim().length}/1000 caracteres (mínimo 15)`}
        >
          <textarea
            className="form-control"
            rows={5}
            maxLength={1000}
            value={text}
            data-autofocus
            onChange={(e) => setText(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Devolução referenciada: quantidade por item até o saldo devolvível; a nota de devolução sai direto para a SEFAZ. */
export function ReturnDialog({ open, doc, onClose, onDone }: TextDialogProps): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [qty, setQty] = useState<Record<number, number | null>>({});
  const [reason, setReason] = useState("");
  const { key, renew } = useOpenKey(open);
  useEffect(() => {
    if (open) {
      setQty({});
      setReason("");
    }
  }, [open]);
  const left = new Map(doc.returnable.map((r) => [r.position, r.qtyQ4]));
  const items = Object.entries(qty)
    .map(([p, q]) => ({ position: Number(p), qtyQ4: q ?? 0 }))
    .filter((x) => x.qtyQ4 > 0);
  const over = items.find((x) => x.qtyQ4 > (left.get(x.position) ?? 0));
  const send = useMutation({
    mutationFn: () =>
      api<FiscalDocumentView>(`/fiscal-documents/${doc.id}/return`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ items, ...(reason.trim() ? { reason: reason.trim() } : {}) }),
      }),
    onSuccess: (d) => {
      toast("info", `NF-e de devolução nº ${d.number ?? ""} enviada à SEFAZ`);
      onDone(d);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title="Devolver itens"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending || items.length === 0 || !!over}
            onClick={() => send.mutate()}
          >
            {send.isPending ? "Emitindo…" : "Emitir NF-e de devolução"}
          </button>
        </>
      }
    >
      <p className="small text-muted">
        Sai uma NF-e de entrada (finalidade devolução) referenciando esta nota, com o CFOP de devolução e os
        impostos proporcionais. Os itens voltam ao estoque e o valor fica como crédito a pagar ao cliente.
      </p>
      <div className="table-responsive">
        <table className="table table-sm align-middle" aria-label="Itens a devolver">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Descrição</th>
              <th scope="col" className="text-end">
                Saldo
              </th>
              <th scope="col" className="text-end" style={{ width: "9rem" }}>
                Devolver
              </th>
            </tr>
          </thead>
          <tbody>
            {doc.items.map((it) => {
              const max = left.get(it.position) ?? 0;
              const v = qty[it.position] ?? null;
              const bad = v !== null && v > max;
              return (
                <tr key={it.position}>
                  <td>{it.position}</td>
                  <td>{it.description}</td>
                  <td className="text-end">
                    {formatQty(max)} {it.unit}
                  </td>
                  <td>
                    <QtyInput
                      value={v}
                      decimals={4}
                      disabled={max === 0}
                      aria-label={`Quantidade a devolver do item ${it.position}`}
                      aria-invalid={bad ? "true" : undefined}
                      onChange={(q) => setQty((s) => ({ ...s, [it.position]: q }))}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {over && (
        <div className="alert alert-danger small" role="alert">
          A quantidade do item {over.position} passa do saldo devolvível.
        </div>
      )}
      <div className="row">
        <Field label="Motivo (opcional)" className="col-12" hint="Vai nas informações complementares.">
          <input
            className="form-control"
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

type VoidProps = {
  open: boolean;
  series: number;
  from?: number;
  to?: number;
  onClose(): void;
  onDone(): void;
};

/** Inutilização de faixa da série (no ambiente da empresa): números nunca usados nem autorizados. */
export function VoidRangeDialog({ open, series, from, to, onClose, onDone }: VoidProps): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [range, setRange] = useState({ from: "", to: "" });
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const { key, renew } = useOpenKey(open);
  useEffect(() => {
    if (open) {
      setRange({ from: from ? String(from) : "", to: to ? String(to) : "" });
      setReason("");
      setTouched(false);
    }
  }, [open, from, to]);
  const f = Number(range.from);
  const t = Number(range.to);
  const rangeProblem =
    !Number.isInteger(f) || !Number.isInteger(t) || f < 1 || t < 1
      ? "Informe os números inicial e final."
      : voidRangeProblem({ from: f, to: t });
  const reasonProblem = eventTextProblem(reason, "reason");
  const send = useMutation({
    mutationFn: () =>
      api<unknown>("/fiscal-documents/void-range", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ series, from: f, to: t, reason }),
      }),
    onSuccess: () => {
      toast("info", "Inutilização enviada à SEFAZ");
      onDone();
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title={`Inutilizar numeração da série ${series}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={send.isPending}
            onClick={() => (rangeProblem || reasonProblem ? setTouched(true) : send.mutate())}
          >
            {send.isPending ? "Enviando…" : "Inutilizar"}
          </button>
        </>
      }
    >
      <p className="small text-muted">
        A inutilização avisa a SEFAZ de que estes números não serão usados. Só vale para números nunca
        autorizados; a nota rejeitada da faixa passa a constar como inutilizada.
      </p>
      <div className="row">
        <Field
          label="Número inicial"
          required
          className="col-6"
          error={touched ? (rangeProblem ?? undefined) : undefined}
        >
          <input
            className="form-control"
            inputMode="numeric"
            value={range.from}
            data-autofocus
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value.replace(/\D/g, "") }))}
          />
        </Field>
        <Field label="Número final" required className="col-6">
          <input
            className="form-control"
            inputMode="numeric"
            value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value.replace(/\D/g, "") }))}
          />
        </Field>
        <Field
          label="Justificativa"
          required
          className="col-12"
          error={touched ? (reasonProblem ?? undefined) : undefined}
          hint={`${reason.trim().length}/255 caracteres (mínimo 15)`}
        >
          <textarea
            className="form-control"
            rows={3}
            maxLength={255}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Linha do tempo dos eventos (mais novo primeiro), com XML/PDF do registrado e "Consultar de novo". */
export function EventsTimeline({
  events,
  canUpdate,
  onChanged,
}: {
  events: FiscalEventView[];
  canUpdate: boolean;
  onChanged(): void;
}): ReactElement | null {
  const { api } = useSession();
  const toast = useToast();
  const download = useMutation({
    mutationFn: (x: { id: string; file: "xml" | "pdf" }) =>
      api<FiscalFileLink>(`/fiscal-documents/events/${x.id}/${x.file}`),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const refresh = useMutation({
    mutationFn: (id: string) => api<unknown>(`/fiscal-documents/events/${id}/refresh`, { method: "POST" }),
    onSuccess: () => {
      toast("info", "Consultando a SEFAZ de novo");
      onChanged();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  if (events.length === 0) return null;
  return (
    <ol className="list-unstyled mb-0" aria-label="Eventos da nota">
      {events.map((e) => {
        const msg = sefazMessage(e.statusCode, e.statusMessage);
        return (
          <li
            key={e.id}
            className="border-start border-3 ps-3 pb-3 position-relative"
            data-testid="fiscal-event"
          >
            <div className="d-flex flex-wrap align-items-center gap-2">
              <strong>{eventTitle(e)}</strong>
              <StatusBadge tone={EVENT_STATUS_TONE[e.status]}>{EVENT_STATUS_LABEL[e.status]}</StatusBadge>
              {eventInFlight(e) && (
                <span className="spinner-border spinner-border-sm text-info" role="status">
                  <span className="visually-hidden">Aguardando a SEFAZ</span>
                </span>
              )}
              <span className="text-muted small">
                {dateTimeBR(e.registeredAt ?? e.createdAt)}
                {e.protocol && ` — protocolo ${e.protocol}`}
              </span>
            </div>
            {e.text && (
              <p className="mb-1 small" style={{ whiteSpace: "pre-wrap" }}>
                {e.text}
              </p>
            )}
            {msg && e.status !== "registered" && (
              <div className={`small ${e.status === "rejected" ? "text-danger" : "text-muted"}`}>{msg}</div>
            )}
            <div className="d-flex flex-wrap gap-2 mt-1">
              {e.hasPdf && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={download.isPending}
                  onClick={() => download.mutate({ id: e.id, file: "pdf" })}
                >
                  Baixar PDF
                </button>
              )}
              {e.hasXml && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={download.isPending}
                  onClick={() => download.mutate({ id: e.id, file: "xml" })}
                >
                  Baixar XML
                </button>
              )}
              {canUpdate && e.canRefresh && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={refresh.isPending}
                  onClick={() => refresh.mutate(e.id)}
                >
                  Consultar de novo
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Notas ligadas: a devolvida (NFref) e as devoluções desta nota. */
export function LinkedInvoices({ doc }: { doc: FiscalDocumentView }): ReactElement | null {
  if (doc.references.length === 0 && doc.returns.length === 0) return null;
  return (
    <ul className="list-unstyled mb-0 small">
      {doc.references.map((r) => (
        <li key={r.accessKey}>
          Devolução da{" "}
          {r.id ? (
            <Link to="/vendas/notas-fiscais/$id" params={{ id: r.id }}>
              NF-e nº {r.number} (série {r.series})
            </Link>
          ) : (
            <span className="font-monospace">NF-e {r.accessKey}</span>
          )}
        </li>
      ))}
      {doc.returns.map((r) => (
        <li key={r.id} className="d-flex flex-wrap align-items-center gap-2">
          <Link to="/vendas/notas-fiscais/$id" params={{ id: r.id }}>
            {r.number === null ? "Devolução (rascunho)" : `Devolução nº ${r.number} (série ${r.series ?? 1})`}
          </Link>
          <StatusBadge tone={INVOICE_STATUS_TONE[r.status]}>{INVOICE_STATUS_LABEL[r.status]}</StatusBadge>
        </li>
      ))}
    </ul>
  );
}
